import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import StandardInput from "../../components/basic/StandardInput"
import Button from "../../components/basic/Button"
import { api } from '../../services/apiClient'
import loaderWhiteImg from 'src/assets/loader-white.svg'
import {
  Container,
  Title,
  ContentText,
  FooterMessage,
  BoldBasicTitle
} from './styles'
import { FlexDiv } from 'src/components/application/formComponents/styles'
import { tlieSources } from 'src/constants/tlie'
import { AxiosError } from 'axios'
import { authErrorMessages } from 'src/constants/general'
import ReminderCard from 'src/components/basic/ReminderCard'

const Unauthenticated = () => {
  const { search, state } = useLocation()
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [promoteError, setPromoteError] = useState(false)
  const [isTakingAction, setIsTakingAction] = useState(false)

  const isPolicyReady = state as { policyReady: boolean | null } | null

  const handleSendMagicLink = async () => {
    setIsTakingAction(true)
    setMessage('')
    setIsSuccess(false)
    setPromoteError(false)
    if(email) {
      if(isPolicyReady && isPolicyReady?.policyReady === true) {
        try {
          await api.post('/Applicants/policyReady',{}, { params: { email } })
          await api.post('/MagicLinks', { email })
          setIsSuccess(true)
        } catch(e) {
          const error = e as AxiosError<{errorMessage: string}>
          setMessage(authErrorMessages[error?.response?.data?.errorMessage ?? 'default'] ?? authErrorMessages['default'])
        }
      } else {
        try {
          let payload: { email: string, redirect?: string } = { email }
          if(search) {
            payload['redirect'] = search.replace('?redirect=', '')
          }
          await api.post('/MagicLinks', payload)
          setIsSuccess(true)
        } catch(e) {
          const error = e as AxiosError<{errorMessage: string}>
          if(error?.response?.data?.errorMessage === 'your application is still under review' || error?.response?.data?.errorMessage?.includes('PROMOTION_ERROR')) {
            setPromoteError(true)
          } else {
            setMessage(authErrorMessages[error?.response?.data?.errorMessage ?? 'default'] ?? authErrorMessages['default'])
          }
        }
      }
    } else {
      setMessage('Please, type in your e-mail so you can get a new authentication link.')
    }
    setIsTakingAction(false)
  }

  const titleText = () => {
    if(isPolicyReady && isPolicyReady?.policyReady === true)
      return "Welcome to TLI's online application portal!"

    if(search) {
      const path = search.replace('?redirect=', '')
      if((!path.includes('/admin') && !path.includes('/customer')) || path.includes('/renew')) {
        return "Welcome back to TLI's online application portal!"
      }
    }
    
    return "Welcome back to TLI's online application portal!"
  }

  const contentText = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
        <p>For security reasons, your session has expired.</p>
        <p><b>To sign back in, please:</b></p>
        <ol style={{ marginLeft: '2rem' }}>
          <li>Enter the <b>same email address</b> you used as the main firm contact on your <b>most recent TLI application</b> below.</li>
        </ol>
        <p>Click Send Link.</p>
        <ol style={{ marginLeft: '2rem' }}>
          <li style={{  visibility: 'hidden', lineHeight: 0 }}></li>
          <li>Check that email inbox immediately for the new sign-in link.
            <ul style={{ paddingTop: '0.5rem' }}>
              <li>If you don't see it, please check your <b>Spam</b> or <b>Junk</b> folder.</li>
            </ul>
          </li>
        </ol>
        <BoldBasicTitle>Still Need Help?</BoldBasicTitle>
        <p>
          If you still can't sign in, or if you're not sure which email you used, please contact our member services team at <a href="mailto:info@tlie.org">info@tlie.org</a>. Include a description of the issue, and we'll get back to you within 2 business days. 
        </p>
        <p><b>Phone support:</b> (800) 252-9332  </p>

        <BoldBasicTitle>Need to submit your application right now?</BoldBasicTitle>
        <p>
          You can complete the <b>fillable PDF</b> versions of the application (<a href="https://www.tlie.org/wp-content/uploads/2025/07/FINAL-New-Business-Application-2.pdf">new business</a> or <a href="https://www.tlie.org/wp-content/uploads/2024/12/LPL_Renew_App-0-30.pdf">renewal</a>) and email it for faster processing. Please send completed PDF applications to: <a href="mailto:app@tlie.org">app@tlie.org</a>
        </p>
        <p>
          Please note that this portal is for your insurance application and policy information only. If you are attempting to access our <b>CLE courses</b>. Those are found on our LearnUpon site <a href="https://tlie.learnupon.com/store">here</a>.
        </p>
      </div>
    );
  }

  return (
    <Container>
      <Title>
        {titleText()}
      </Title>
      <ContentText>
        {contentText()}
      </ContentText>
      <FlexDiv dir='column' gap="16px" margin="80px 0 0 0">
        <StandardInput
          type="email"
          label='Email'
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => {
            if(e.key === 'Enter' && !isTakingAction) {
              e.preventDefault()
              handleSendMagicLink()
            }
          }}
          isIncorrect={!!message}
          errorMessage={!!message ? <span style={{whiteSpace: 'normal'}}>{message}</span> : null}
        />
        <Button onClick={handleSendMagicLink} isTakingAction={isTakingAction} style={{ width: '100%' }}>
          {isTakingAction ? <img src={loaderWhiteImg} alt="loading"/> : 'Send Link'}
        </Button>
        {isSuccess === true ? (
          <ReminderCard title="E-mail sent!" icon='bulb'>
            We have sent you a new authentication link. Please check your inbox and click the new link to continue.
          </ReminderCard>
        ) : null}
        {promoteError === true ? (
          <ReminderCard title="Application under review" icon='warning'>
            The application you have sent is still under review by our team. When everything is ready, we will send you an e-mail.
          </ReminderCard>
        ) : null}
      </FlexDiv>
      <FooterMessage>
        Be sure to check your email’s spam and junk folders if you do not see an email from us. If you still have not received a login link, please reach out to customer service at {tlieSources.formattedPhone} for assistance.
      </FooterMessage>
    </Container>
  )
}

export default Unauthenticated